import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const GAMES = [
  { key: 'grille',     label: 'Grille de Départ',  emoji: '🏁', target: 1, path: '/grille',     color: '#06b6d4' },
  { key: 'guess',      label: 'Qui Suis-Je ?',     emoji: '🕵️', target: 4, path: '/guess',      color: '#a855f7' },
  { key: 'gap',        label: 'Le Gap',            emoji: '⏱️', target: 4, path: '/gap',        color: '#f59e0b' },
  { key: 'lead',       label: 'Qui Menait ?',      emoji: '🏆', target: 4, path: '/lead',       color: '#3b82f6' },
  { key: 'quiz',       label: 'Circuit Quiz',      emoji: '🗺️', target: 4, path: '/quiz',       color: '#e10600' },
  { key: 'lightspeed', label: 'Lightspeed',        emoji: '⚡', target: 4, path: '/lightspeed', color: '#facc15' },
  { key: 'podium',     label: 'Podium',            emoji: '🥇', target: 4, path: '/podium',     color: '#fb923c' },
];

const DAILY_XP_GOAL = 10000;

const DailyGoals = () => {
  const { user } = useAuth();
  const [open, setOpen]       = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/game/daily-progress');
      setData(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) fetch();
  }, [open, fetch]);

  if (!user) return null;

  const progress = data?.progress || {};
  const totalXp  = data?.totalXp  || 0;

  const gamesStarted  = GAMES.filter(g => (progress[g.key]?.plays || 0) >= 1).length;
  const gamesComplete = GAMES.filter(g => (progress[g.key]?.plays || 0) >= g.target).length;
  const allDone       = gamesComplete === GAMES.length;
  const xpPct         = Math.min((totalXp / DAILY_XP_GOAL) * 100, 100);

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        title="Objectifs du jour"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
        style={{ background: allDone ? '#22c55e' : '#18181b', border: '2px solid #3f3f46' }}
      >
        <div className="relative">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={allDone ? '#22c55e' : '#a1a1aa'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="6" height="6" rx="1" />
            <path d="M3 14h6M3 19h6" />
            <path d="M13 8h8M13 14h8M13 19h8" />
            <polyline points="5 8 6.5 9.5 9 7" stroke={allDone ? '#22c55e' : '#a1a1aa'} strokeWidth="1.8" />
          </svg>
          {!allDone && gamesStarted > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-f1red flex items-center justify-center text-[9px] font-black text-white">
              {gamesStarted}
            </span>
          )}
        </div>
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-zinc-800">
              <div>
                <h2 className="text-white font-black text-lg">🎯 Objectifs du jour</h2>
                <p className="text-zinc-500 text-xs mt-0.5">
                  {gamesComplete}/{GAMES.length} jeux complétés · <span className="text-white font-bold">{totalXp.toLocaleString()} XP</span> gagnés aujourd'hui
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white text-xl leading-none transition-colors">✕</button>
            </div>

            {/* XP progress bar */}
            <div className="px-5 py-3 border-b border-zinc-800">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-zinc-500">Objectif {DAILY_XP_GOAL.toLocaleString()} XP/jour</span>
                <span className="font-bold" style={{ color: xpPct >= 100 ? '#22c55e' : '#e10600' }}>
                  {Math.round(xpPct)}%
                </span>
              </div>
              <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${xpPct}%`, background: xpPct >= 100 ? '#22c55e' : 'linear-gradient(90deg,#e10600,#f97316)' }}
                />
              </div>
            </div>

            {/* Game list */}
            <div className="px-5 py-3 flex flex-col gap-2 max-h-96 overflow-y-auto">
              {loading ? (
                <p className="text-zinc-500 text-sm text-center py-6">Chargement...</p>
              ) : (
                GAMES.map(g => {
                  const plays  = progress[g.key]?.plays || 0;
                  const xp     = progress[g.key]?.xp    || 0;
                  const done   = plays >= g.target;
                  const started = plays >= 1;

                  return (
                    <div key={g.key}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors"
                      style={{ borderColor: done ? g.color + '55' : '#3f3f46', background: done ? g.color + '0d' : 'transparent' }}
                    >
                      {/* Check / emoji */}
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm"
                        style={{ background: done ? g.color + '22' : '#27272a' }}>
                        {done ? <span style={{ color: g.color }}>✓</span> : <span className="opacity-70">{g.emoji}</span>}
                      </div>

                      {/* Name + dots */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white leading-tight">{g.label}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {Array.from({ length: g.target }).map((_, i) => (
                            <div key={i} className="w-2 h-2 rounded-full transition-colors"
                              style={{ background: i < plays ? g.color : '#3f3f46' }} />
                          ))}
                          <span className="text-[10px] text-zinc-500 ml-1">{plays}/{g.target}</span>
                        </div>
                      </div>

                      {/* XP + play button */}
                      <div className="flex items-center gap-2 shrink-0">
                        {xp > 0 && (
                          <span className="text-[11px] font-black" style={{ color: g.color }}>+{xp.toLocaleString()} XP</span>
                        )}
                        {!done && (
                          <Link to={g.path} onClick={() => setOpen(false)}
                            className="text-[11px] font-black px-2.5 py-1 rounded-lg transition-colors text-white"
                            style={{ background: started ? g.color + '33' : g.color, color: started ? g.color : 'white' }}>
                            {started ? 'Continuer' : 'Jouer'}
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-zinc-800">
              {allDone ? (
                <p className="text-center text-green-400 font-black text-sm">🏆 Objectif du jour atteint !</p>
              ) : (
                <p className="text-zinc-600 text-xs text-center">
                  Joue à chaque jeu 4× (1× pour Grille) pour maximiser ton XP quotidien
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DailyGoals;
